import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  fetchOtoAssessmentById,
  fetchOtoAssessmentResults,
  fetchOtoRelevanceThresholds,
  type OtoAssessmentListItem,
  type OtoAssessmentResults,
  type OtoRelevanceThresholds,
} from "../../../services/objectToObjectAssessmentService";
import { getAiHubStatusChipSx, type AiHubStatusTone } from "../aiHubChipStyles";
import { AI_HUB_PATH } from "../constants";
import AiHubDetailBreadcrumb from "../AiHubDetailBreadcrumb";
import AssessmentStatusChip from "./AssessmentStatusChip";
import ObjectAnalysisGroupCard from "./ObjectAnalysisGroupCard";
import {
  formatDetailSummaryLine,
  mapResultsToDetail,
  type ObjectAnalysisAssessmentDetail,
} from "./objectAnalysisDetailData";

const DEFAULT_THRESHOLDS: OtoRelevanceThresholds = {
  high: 0.67,
  medium: 0.34,
};

const STATUS_TONES: Record<
  ObjectAnalysisAssessmentDetail["statusTone"],
  AiHubStatusTone
> = {
  completed: "green",
  failed: "red",
  partial: "yellow",
  inProgress: "blue",
};

function loadErrorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

export default function ObjectAnalysisDetailPage() {
  const navigate = useNavigate();
  const { assessmentId = "" } = useParams();
  const trimmedId = assessmentId.trim();
  const [direction, setDirection] = useState<{
    assessmentId: string;
    objectType: string;
  } | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<string[] | null>(null);
  const [thresholds, setThresholds] =
    useState<OtoRelevanceThresholds>(DEFAULT_THRESHOLDS);
  const [assessment, setAssessment] = useState<OtoAssessmentListItem | null>(
    null,
  );
  const [results, setResults] = useState<OtoAssessmentResults | null>(null);
  const [loadingAssessment, setLoadingAssessment] = useState(true);
  const [loadingResults, setLoadingResults] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const groupbyObjectType =
    direction?.assessmentId === trimmedId ? direction.objectType : "";
  const scopedAssessment =
    assessment && assessment.taskId === trimmedId ? assessment : null;
  const scopedResults =
    results && results.taskId === trimmedId ? results : null;

  useEffect(() => {
    let cancelled = false;
    fetchOtoRelevanceThresholds()
      .then((next) => {
        if (!cancelled) setThresholds(next);
      })
      .catch(() => {
        /* keep defaults */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setExpandedKeys(null);

    if (!trimmedId) {
      setError("Assessment id is required.");
      setLoadingAssessment(false);
      return;
    }

    setLoadingAssessment(true);
    setError(null);

    fetchOtoAssessmentById(trimmedId)
      .then((next) => {
        if (cancelled) return;
        const nextGroupby =
          next?.objectA.objectType || next?.objectB.objectType || "";
        if (!nextGroupby) {
          throw new Error("Unable to determine group-by object type.");
        }
        setAssessment(next);
        setDirection((current) =>
          current?.assessmentId === trimmedId && current.objectType
            ? current
            : { assessmentId: trimmedId, objectType: nextGroupby },
        );
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setAssessment(null);
        setError(loadErrorMessage(err, "Failed to load assessment results."));
      })
      .finally(() => {
        if (!cancelled) setLoadingAssessment(false);
      });

    return () => {
      cancelled = true;
    };
  }, [trimmedId]);

  useEffect(() => {
    let cancelled = false;
    if (!trimmedId || !groupbyObjectType) {
      return;
    }

    setLoadingResults(true);
    setError(null);

    fetchOtoAssessmentResults(trimmedId, groupbyObjectType)
      .then((next) => {
        if (cancelled) return;
        setResults(next);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setResults(null);
        setError(loadErrorMessage(err, "Failed to load assessment results."));
      })
      .finally(() => {
        if (!cancelled) setLoadingResults(false);
      });

    return () => {
      cancelled = true;
    };
  }, [trimmedId, groupbyObjectType]);

  const detail = useMemo(() => {
    if (!scopedResults) return null;
    return mapResultsToDetail(
      scopedResults,
      thresholds,
      scopedAssessment?.createdAt,
      scopedAssessment?.completedCount ?? 0,
      scopedAssessment,
    );
  }, [scopedResults, thresholds, scopedAssessment]);

  const groups = detail?.groups ?? [];
  const openKeys = expandedKeys ?? groups.map((group) => group.key);
  const activeDirection =
    detail?.directions.find((item) => item.objectType === groupbyObjectType) ??
    detail?.directions[0];

  const toggleGroup = (key: string) => {
    setExpandedKeys(
      openKeys.includes(key)
        ? openKeys.filter((openKey) => openKey !== key)
        : [...openKeys, key],
    );
  };

  const returnToList = () => {
    navigate(AI_HUB_PATH, {
      state: { tab: "objectToObjectAnalysisResults" },
    });
  };

  const summaryLine = useMemo(
    () => (detail ? formatDetailSummaryLine(detail) : ""),
    [detail],
  );

  if (!detail && !error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f7f8fa",
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error && !detail) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#f7f8fa", p: 3 }}>
        <AiHubDetailBreadcrumb
          currentLabel={assessmentId || "Assessment"}
          onBack={returnToList}
        />
        <Typography sx={{ mt: 2, color: "#CF1322" }}>{error}</Typography>
      </Box>
    );
  }

  if (!detail) return null;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f7f8fa",
        p: { xs: 2, md: 3 },
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ mb: 2 }}>
        <AiHubDetailBreadcrumb
          currentLabel={assessmentId}
          onBack={returnToList}
        />
      </Box>

      <Box
        sx={{
          bgcolor: "#fff",
          border: "1px solid #F2F2F2",
          borderRadius: "8px",
          p: { xs: 2, md: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h1"
              sx={{
                fontSize: 20,
                lineHeight: "28px",
                fontWeight: 600,
                color: "#000",
              }}
            >
              {assessmentId}
              {detail.statusTone === "failed" ? (
                <AssessmentStatusChip
                  component="span"
                  label={detail.status}
                  tone="red"
                  showInfoIcon
                  hint={detail.errorMessage}
                  sx={{ ml: 1, verticalAlign: "middle" }}
                />
              ) : (
                <Chip
                  component="span"
                  size="small"
                  label={detail.status}
                  sx={{
                    ...getAiHubStatusChipSx(STATUS_TONES[detail.statusTone]),
                    ml: 1,
                    verticalAlign: "middle",
                  }}
                />
              )}
            </Typography>
            <Typography
              sx={{ fontSize: 14, lineHeight: "24px", color: "#000000" }}
            >
              {summaryLine}
            </Typography>
          </Box>

          {detail.directions.length > 0 ? (
            <TextField
              select
              size="small"
              value={groupbyObjectType || detail.directions[0]?.objectType || ""}
              sx={{ width: 220, "& .MuiInputBase-root": { height: 40 } }}
              slotProps={{
                select: { SelectDisplayProps: { "aria-label": "Direction" } },
              }}
              onChange={(event) => {
                setDirection({
                  assessmentId: trimmedId,
                  objectType: event.target.value,
                });
                setExpandedKeys(null);
              }}
            >
              {detail.directions.map((option) => (
                <MenuItem key={option.objectType} value={option.objectType}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
        </Box>

        <Box
          sx={{
            mt: 3,
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          {loadingResults || loadingAssessment ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : groups.length === 0 ? (
            <Typography sx={{ color: "#8c8c8c", fontSize: 14 }}>
              No grouped results available.
            </Typography>
          ) : (
            groups.map((group) => (
              <ObjectAnalysisGroupCard
                key={group.key}
                group={group}
                targetLabel={activeDirection?.targetLabel || "Object"}
                expanded={openKeys.includes(group.key)}
                onToggle={() => toggleGroup(group.key)}
              />
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
}
